import { z } from 'zod';

import { EventFormat, EditableEvent, EditableSession, T2DomainEnum } from 'types/event.types';
import dayjs from 'utils/extendedDayJS';
import { FULL_DATE_FORMAT } from 'constants/dates';

const validateEventDescriptionNotEmpty = (value: string) => {
  return (
    value
      .replace(/<[^>]*>/g, '') // Remove HTML tags
      .replace(/&[^;]+;/g, '') // Remove HTML entities (e.g. &amp;, &quot;, etc.)
      .trim().length > 0
  );
};

const getEventFormatFromApi = (event: EditableEvent | EditableSession): EventFormat | undefined => {
  if (event.isInPerson && event.isVirtual) {
    return EventFormat.HYBRID;
  }
  if (event.isInPerson) {
    return EventFormat.IN_PERSON;
  } else if (event.isVirtual) {
    return EventFormat.VIRTUAL;
  }

  return undefined;
};

/**
 * Responsible for converting db absolute time to form value for:
 * Feedback settings - schedule
 * Reminder form - schedule
 * @param sendAt
 * @param eventTimezone
 * @returns
 */
export const convertSendAtToFormValue = (sendAt: string, eventTimezone?: string): Date => {
  // For incoming data:
  // 1. Date is in UTC (from DB)
  // 2. Convert to event's timezone
  // 3. Replace timezone to browser's timezone (keep wall-clock)
  const inEventTz = dayjs.convertToTimezone(sendAt, eventTimezone ?? 'UTC');
  const wallClockTime = inEventTz.format(FULL_DATE_FORMAT);
  return dayjs.tz(wallClockTime, dayjs.tz.guess()).toDate();
};

/**
 * Responsible for converting absolute time to DB value
 * Feedback settings - schedule
 * Reminder form - schedule
 * @param sendAt
 * @param eventTimezone
 * @returns
 */
export const convertSendAtToDBValue = (sendAt: Date, eventTimezone?: string): string => {
  // For outgoing data:
  // 1. Date is in browser's timezone (from DateTimePickerOriginal)
  // 2. Replace timezone to event's timezone (keep wall-clock)
  // 3. Convert to UTC for DB
  const wallClockDate = dayjs(sendAt).format(FULL_DATE_FORMAT);
  const inEventTz = dayjs.tz(wallClockDate, eventTimezone ?? 'UTC').toDate();
  return dayjs.formatForDB(inEventTz);
};

const createT2DomainRefinement = (
  t2DomainEnabled: boolean
): ((data: { t2DomainArray?: T2DomainEnum[] | null }, ctx: z.RefinementCtx) => void) => {
  return (data, ctx): void => {
    if (t2DomainEnabled && (!data.t2DomainArray || data.t2DomainArray.length === 0)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Domain selection is required',
        path: ['t2DomainArray']
      });
    }
  };
};

export { validateEventDescriptionNotEmpty, getEventFormatFromApi, createT2DomainRefinement };
